import React, { useEffect, useState } from 'react';
import { style, classes } from 'typestyle';
import * as appHelpers from '@src/services/helpers/appHelpers';
import { Pathname } from '@src/types/Browsing';
import { auth } from '@src/config/firebaseAuth';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { api } from '../../services/api';
import { LoginTextInput } from './TextInput';
import { LoginSigninLabel } from './SigninLabel';
import { LoginSubmitButton } from './LoginSubmitButton';
import Captcha from './Captcha';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

interface IProps {
  oobCode: string
}

interface inputErrors {
  lowerCase: boolean,
  upperCase: boolean,
  number: boolean,
  longEnough: boolean,
  specialChar: boolean,
  confirmation: boolean,
}

export default function FormPasswordInput(props: IProps): JSX.Element {
  const specialCharsList = '(#$%.*+-&@<>!?)';

  const minCharsLength = 8;

  const [captchaToken, setCaptchaToken] = useState<string | undefined>(undefined);
  const [password, setPassword] = useState<string>("");
  const [passwordConfirmation, setpasswordConfirmation] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errors, setErrors] = useState<inputErrors>({
    lowerCase: true,
    upperCase: true,
    number: true,
    longEnough: true,
    specialChar: true,
    confirmation: true,
  });
  useEffect(() => {
    validatePassword()
  }, [password, passwordConfirmation]);

  function handleCaptchaTokenChange(token: string) {
    setCaptchaToken(token);
  }

  function passwordIsValid(): boolean {
    return (Object.values(errors).indexOf(false) === -1);
  }

  function passwordConfirmationIsValid(): boolean {
    return errors.confirmation;
  }

  async function updatePasswordRequest(): Promise<void> {
    if (!captchaToken || !passwordIsValid()) {
      return;
    }

    setIsSubmitting(true);
   
    try {
      await verifyPasswordResetCode(auth, props.oobCode);
      await api.setPasswordUpdate(props.oobCode, captchaToken);
      await confirmPasswordReset(auth, props.oobCode, password);
      appHelpers.addAlert({ text: 'changepassword.passwordchanged', type: 'success' });
      appHelpers.setRoute(Pathname.login);
    } catch (e: any) {
      console.error('confirmPassword', e);

      if (['auth/expired-action-code', 'auth/invalid-action-code'].indexOf(e.code) >= 0) {
        appHelpers.addAlert({ text: 'common.error.expired-action-code', type: 'danger' });
        appHelpers.setRoute(Pathname.root);
        setTimeout(() => {
          appHelpers.setRoute(Pathname.root);
          appHelpers.setRoute(Pathname.changePassword);
        }, (200));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  function handlePassword(pass: string) {
    setPassword(pass);
  }

  function handlePasswordConfirm(confPass: string) {
    setpasswordConfirmation(confPass);
  }

  function isLongEnough(): boolean {
    return password.length >= minCharsLength;
  }

  function hasUppercase(): boolean {
    return /[A-Z]/.test(password);
  }

  function hasSpecialChar(): boolean {
    return specialCharsList.split('').map((char) => password.indexOf(char) >= 0).reduce((prev, curr) => curr || prev, false);
  }

  function hasLowercase(): boolean {
    return /[a-z]/.test(password);
  }

  function hasNumber(): boolean {
    return /[0-9]/.test(password);
  }

  function checkPasswordValidation(): boolean {
    return password === passwordConfirmation;
  }

  function validatePassword() {
    let errors = {
      lowerCase: hasLowercase(),
      upperCase: hasUppercase(),
      number: hasNumber(),
      longEnough: isLongEnough(),
      specialChar: hasSpecialChar(),
      confirmation: checkPasswordValidation(),
    }
    setErrors(errors);
  }

  const submitBtnDisabled = !passwordIsValid() || !passwordConfirmationIsValid() || !captchaToken || isSubmitting;

  return (
    <div>
      <div className="col-sm-10 offset-sm-1">
        <LoginSigninLabel title={window.T('changepassword.title')} />

        <div style={{ position: 'relative' }}>
          {!passwordIsValid() ? (
            <span className={classes('password-error-popup', errorPopupClass)}>
              Your password must:
              {!errors.longEnough ? (
                <li>
                  include at least
                  {' '}
                  {minCharsLength}
                  {' '}
                  characters
                </li>
              ) : null }
              {!errors.lowerCase ? (
                <li>
                  include at least one lowercase letter
                </li>
              ) : null }
              {!errors.upperCase ? (
                <li>
                  include at least one uppercase letter
                </li>
              ) : null }
              {!errors.number ? (
                <li>
                  include at least one number
                </li>
              ) : null }
              {!errors.specialChar ? (
                <li>
                  include at least one special character
                  {' '}
                  {specialCharsList}
                </li>
              ) : null }
              {!errors.confirmation ? (
                <li>
                  match your confirmation password
                </li>
              ) : null }
            </span>
          ) : null }
          <LoginTextInput
            error={!passwordIsValid()}
            label="password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            onChange={({ target }) => handlePassword(target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                    style={{ color: '#8a8a8a' }}
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>
        <LoginTextInput
          error={!passwordConfirmationIsValid()}
          label="password confirmation"
          type={showConfirmation ? 'text' : 'password'}
          fullWidth
          onChange={({ target }) => handlePasswordConfirm(target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmation((v) => !v)}
                  edge="end"
                  size="small"
                  style={{ color: '#8a8a8a' }}
                >
                  {showConfirmation ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Captcha
          onTokenChange={(token) => handleCaptchaTokenChange(token)}
        />
        <div style={{ marginTop: 7, display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: 160 }}>
            <LoginSubmitButton
              disabled={submitBtnDisabled}
              onClick={() => updatePasswordRequest()}
              text={window.T('submit').toUpperCase()}
            />
          </div>
        </div>
      </div>
    </div>
  );

}

const errorPopupClass = style({
  backgroundColor: 'white',
  display: 'flex',
  color: '#555',
  padding: 8,
  flexDirection: 'column',
  fontSize: 10,
  fontWeight: 300,
  textAlign: 'left',
  borderRadius: 4,
  position: 'absolute',
  left: -360,
  width: 330,
  top: 0,
  margin: 15,
});
